/**
 * Página do setor no Mapa da Produção (Legado): /legado/mapa/setor/:id.
 *
 * A rota passou a ser por ID do setor: na v3 o setor é CADASTRO (o gestor
 * desenha a sala no mapa geral, dá o nome que a fábrica usa e liga a sala a
 * uma ETAPA da planilha). O link antigo por código fixo (/setor/corte) continua
 * funcionando: ele é redirecionado para o setor que recebe a etapa
 * correspondente.
 *
 * A ETAPA de cada ambiente continua vindo da planilha (data preenchida = etapa
 * finalizada, setores.js); o SETOR pode ser o da etapa ou um que o gestor
 * escolheu à mão. Dentro do setor, o gestor distribui o ambiente em
 * subsetor/posto, marca em produção/pausado, define prazos, cria tarefas,
 * DESMEMBRA PEÇAS do ambiente e organiza a planta na visão Mapa — tudo gravado
 * no backend do Mapa e lido de volta a cada 15 s (useEstadoMapa), para a tela
 * "andar" sozinha quando outro gestor aponta algo no tablet.
 *
 * Mapa de integração: planilha (useDados) + /estado → catálogo de setores →
 * combinarDemandas + combinarPecas → filtros (os mesmos do mapa geral) →
 * visões (kanban/mapa/tabela/calendário/linha do tempo) por entidade
 * (ambiente/lote/tarefa/peça) → painéis de detalhe → escritas
 * (useAcoesDoSetor) → /estado de novo. Permissões: view para entrar, operar
 * para apontar, gerir para configurar, desenhar e cancelar/reabrir.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MotionConfig } from "framer-motion";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AmbienteDetalhesDrawer from "../../../components/AmbienteDetalhesDrawer";
import { EmptyState } from "../../../components/EmptyState";
import { PageLoader } from "../../../components/LoadingMarcenaria";
import { useAuth } from "../../../context/AuthContext";
import { useDados } from "../../../context/DadosContext";
import { useThemeToggle } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";
import { ehConflito, listarPecas, listarTarefas } from "../../../services/mapaProducao";
import { plural } from "../../../utils/dinheiro";
import { F } from "../../../utils/gerencialFields";
import useHoje from "../dados/useHoje";
import { criarCatalogoSetores, derivarDemandas } from "../setores";
import { apelidosDaPlanilha } from "../dados/chaves";
import { combinarDemandas, indexarEstado } from "../dados/combinar";
import { ETAPA_DO_SETOR_FIXO_V2 } from "../dados/etapasPlanilha";
import { aplicarFiltros, contarAtivos } from "../dados/filtros";
import { agruparPorLote } from "../dados/lotes";
import { combinarPecas, pecasDoAmbiente as pecasDoAmbienteChave } from "../dados/pecas";
import useEstadoMapa from "../dados/useEstadoMapa";
import useFiltrosDoMapa from "../dados/useFiltrosDoMapa";
import {
  PERMISSAO,
  ROTULO_STATUS_AMBIENTE,
  ROTULO_STATUS_PECA,
  ROTULO_STATUS_TAREFA,
  tarefaAberta,
  tarefaAtrasada,
} from "../dados/vocabulario";
import BarraDoSetor from "./BarraDoSetor";
import CabecalhoDoSetor, { montarKpis } from "./Cabecalho";
import CalendarioSetor from "./CalendarioSetor";
import { ConteudoAmbiente, ConteudoLote, ConteudoPeca, ConteudoTarefa } from "./Cartoes";
import ConfigurarSetor from "./ConfigurarSetor";
import DesmembrarPeca from "./DesmembrarPeca";
import DetalheAmbiente from "./DetalheAmbiente";
import DetalheLote from "./DetalheLote";
import DetalhePeca from "./DetalhePeca";
import DetalheTarefa from "./DetalheTarefa";
import Equipe from "./Equipe";
import Kanban from "./Kanban";
import MapaDoSetor from "./MapaDoSetor";
import {
  atividadeDoColaborador,
  colaboradoresDoSetor,
  demandasDoSetor,
  distribuirNoKanban,
  idsEmUso,
  lugarDaDemanda,
  lugarDaPeca,
  lugarDaTarefa,
  montarColunas,
  ordenarDemandas,
  ordenarPecas,
  ordenarTarefas,
  pecasDoSetor,
  resumoDoSetor,
  tarefasDoSetor,
} from "./modelo";
import NovaTarefa from "./NovaTarefa";
import { ChipsDosFiltros } from "./PainelFiltros";
import usePreferenciasDoSetor from "./preferencias";
import { Aviso, Pagina } from "./styles";
import Tabela from "./Tabela";
import TimelineSetor from "./Timeline";
import useHistoricoDoSetor from "./useHistoricoDoSetor";
import useAcoesDoSetor from "./useAcoesDoSetor";

const ROTA_MAPA = "/legado/mapa";
const ROTA_PLANILHAS = "/configuracoes/planilhas";

const PAINEL_FECHADO = null;
// O convite "Configurar setor" do mapa geral chega com
// `state: { secao: "configurar" }` (MapaProducao/index.jsx).
const SECAO_CONFIGURAR = "configurar";
const ABA_PADRAO = "setor";
const MAX_SETORES_NO_ERRO = 6;

/**
 * O setor da rota. Por ID; se o que veio na URL for um dos cinco códigos fixos
 * da v2 (corte, bordo, furacao, marcenaria, expedicao), redireciona para o
 * setor que RECEBE a etapa correspondente — o link antigo que está no tablet
 * de alguém, ou num favorito, continua levando ao lugar certo.
 */
function useSetorDaRota(catalogo, carregando) {
  const { id } = useParams();
  const navigate = useNavigate();
  const setor = catalogo.setor(id);
  const etapaLegado = ETAPA_DO_SETOR_FIXO_V2[id] || null;
  const porEtapa = !setor && etapaLegado ? catalogo.setorQueRecebe(etapaLegado) : null;

  useEffect(() => {
    if (porEtapa) navigate(`${ROTA_MAPA}/setor/${porEtapa.id}`, { replace: true });
  }, [porEtapa, navigate]);

  return {
    setor,
    // Sem a primeira leitura do /estado não existe catálogo: dizer "setor não
    // encontrado" aí seria mentira, e o erro piscaria em toda abertura.
    esperando: !setor && (carregando || Boolean(porEtapa)),
  };
}

/**
 * Abre a configuração quando a página chega pelo convite do mapa (só para
 * quem gere) e consome o `state`, para voltar ou recarregar não reabrir.
 *
 * `pronto` = o setor já foi resolvido. Sem essa espera o convite se perdia: na
 * primeira renderização o catálogo ainda não chegou (o /estado é assíncrono),
 * o efeito consumia o `state` sem ter setor nenhum e a configuração nunca
 * abria.
 */
function useAbrirConfiguracaoPelaNavegacao(pronto, podeGerir, abrirConfiguracao) {
  const location = useLocation();
  const navigate = useNavigate();
  const pediuConfigurar = location.state?.secao === SECAO_CONFIGURAR;
  useEffect(() => {
    if (!pediuConfigurar || !pronto) return;
    if (podeGerir) abrirConfiguracao();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [pediuConfigurar, pronto, podeGerir, abrirConfiguracao, navigate, location.pathname, location.search]);
}

function useDerivacoesDoSetor(setorId, catalogo, dados, estado, filtros) {
  const hoje = useHoje();
  // Recalculado a cada carga da planilha E à meia-noite (`useHoje`): atraso é
  // calculado, nunca gravado.
  const planilha = useMemo(() => derivarDemandas(dados, { catalogo, hoje }), [dados, catalogo, hoje]);
  const indice = useMemo(() => indexarEstado(estado), [estado]);
  const combinadas = useMemo(
    () => combinarDemandas(planilha, estado, { catalogo, hoje }),
    [planilha, estado, catalogo, hoje]
  );
  // Todas as peças (não só as deste setor): o painel do ambiente precisa
  // mostrar a peça dele mesmo depois de ela andar para outro setor.
  const todasPecas = useMemo(
    () => combinarPecas(estado, combinadas, { catalogo, hoje }),
    [estado, combinadas, catalogo, hoje]
  );
  const doSetor = useMemo(() => demandasDoSetor(combinadas, setorId), [combinadas, setorId]);
  const tarefas = useMemo(() => tarefasDoSetor(estado.tarefas, setorId), [estado.tarefas, setorId]);
  const pecas = useMemo(() => pecasDoSetor(todasPecas, setorId), [todasPecas, setorId]);
  const colaboradores = useMemo(
    () => colaboradoresDoSetor(estado.colaboradores, setorId),
    [estado.colaboradores, setorId]
  );
  // Dentro do setor, o filtro de setor do mapa geral não vale.
  const filtrosDoSetor = useMemo(() => ({ ...filtros, setores: [] }), [filtros]);
  const filtrado = useMemo(
    () => aplicarFiltros({ demandas: doSetor, tarefas, pecas }, filtrosDoSetor, { estado, hoje }),
    [doSetor, tarefas, pecas, filtrosDoSetor, estado, hoje]
  );
  const estadoDoSetor = useMemo(
    () => ({
      ...estado,
      subsetores: estado.subsetores.filter((s) => s.setor_id === setorId),
      postos: estado.postos.filter((p) => p.setor_id === setorId),
    }),
    [estado, setorId]
  );
  const atividades = useMemo(
    () =>
      new Map(
        estado.colaboradores.map((c) => [
          c.id,
          atividadeDoColaborador(c, { tarefas, demandas: doSetor, postos: estadoDoSetor.postos }),
        ])
      ),
    [estado.colaboradores, tarefas, doSetor, estadoDoSetor.postos]
  );
  return {
    hoje,
    indice,
    combinadas,
    todasPecas,
    doSetor,
    tarefas,
    pecas,
    colaboradores,
    filtrosDoSetor,
    filtrado,
    filtrando: contarAtivos(filtrosDoSetor) > 0,
    estadoDoSetor,
    atividades,
    resumo: resumoDoSetor({ demandas: doSetor, tarefas, colaboradores, pecas }, hoje),
  };
}

function useGruposDoKanban(entidade, colunas, filtrado, pecasCombinadas) {
  return useMemo(() => {
    if (entidade === "tarefa") return distribuirNoKanban(colunas, ordenarTarefas(filtrado.tarefas), lugarDaTarefa);
    if (entidade === "peca") return distribuirNoKanban(colunas, ordenarPecas(filtrado.pecas), lugarDaPeca);
    const porGrupo = distribuirNoKanban(colunas, ordenarDemandas(filtrado.demandas), lugarDaDemanda);
    if (entidade !== "lote") return porGrupo;
    // Lote por lugar: o cartão "Lote 145" da Montagem move só o que está lá.
    return new Map(
      [...porGrupo].map(([id, itens]) => [
        id,
        agruparPorLote(itens, { pecas: pecasCombinadas }).map((lote) => ({ ...lote, grupoId: id })),
      ])
    );
  }, [entidade, colunas, filtrado, pecasCombinadas]);
}

export default function PaginaSetor() {
  const navigate = useNavigate();
  const { dados, meta, initialLoading, erro: erroPlanilha } = useDados() || {};
  const { usuario, hasPermission, request } = useAuth();
  const toast = useToast();
  const { animacoes = true } = useThemeToggle() || {};
  const podeOperar = hasPermission(PERMISSAO.OPERAR);
  const podeGerir = hasPermission(PERMISSAO.GERIR);

  const estadoMapa = useEstadoMapa();
  const { estado } = estadoMapa;
  const catalogo = useMemo(() => criarCatalogoSetores(estado.setores), [estado.setores]);
  const { setor, esperando } = useSetorDaRota(catalogo, estadoMapa.carregando);
  const setorId = setor?.id || null;

  const acoes = useAcoesDoSetor({ request, estadoMapa });
  const { filtros, definirFiltro, removerFiltro, substituirFiltros } = useFiltrosDoMapa();
  const [preferencias, atualizarPreferencias] = usePreferenciasDoSetor();
  const { entidade, visao } = preferencias;

  const d = useDerivacoesDoSetor(setorId, catalogo, dados, estado, filtros);
  // Visões de LISTA (tabela, calendário, linha do tempo) mostram o histórico;
  // Kanban e Mapa são o chão de agora e ficam só com o /estado.
  const visaoDeLista = visao !== "kanban" && visao !== "mapa";
  const historico = useHistoricoDoSetor({
    listar: listarTarefas,
    setorId,
    ativo: Boolean(setorId) && entidade === "tarefa" && visaoDeLista,
    atualizacao: estado,
    atuais: d.tarefas,
  });
  const historicoDePecas = useHistoricoDoSetor({
    listar: listarPecas,
    setorId,
    ativo: Boolean(setorId) && entidade === "peca" && visaoDeLista,
    atualizacao: estado,
    atuais: estado.pecas,
  });
  const pecasDaLista = useMemo(() => {
    if (!historicoDePecas) return d.filtrado.pecas;
    const combinadas = combinarPecas({ ...estado, pecas: historicoDePecas }, d.combinadas, { catalogo, hoje: d.hoje });
    return aplicarFiltros(
      { demandas: d.doSetor, tarefas: d.tarefas, pecas: pecasDoSetor(combinadas, setorId) },
      d.filtrosDoSetor,
      { estado, hoje: d.hoje }
    ).pecas;
  }, [historicoDePecas, d, estado, catalogo, setorId]);
  const tarefasDaLista = useMemo(
    () =>
      historico
        ? aplicarFiltros({ demandas: d.doSetor, tarefas: historico, pecas: d.pecas }, d.filtrosDoSetor, {
            estado,
            hoje: d.hoje,
          }).tarefas
        : d.filtrado.tarefas,
    [historico, d.doSetor, d.pecas, d.filtrosDoSetor, d.filtrado.tarefas, d.hoje, estado]
  );

  const emUso = useMemo(() => idsEmUso(d.doSetor, d.tarefas, d.pecas), [d.doSetor, d.tarefas, d.pecas]);
  const colunas = useMemo(() => montarColunas(setorId, estado, emUso), [setorId, estado, emUso]);
  const grupos = useGruposDoKanban(entidade, colunas, d.filtrado, d.pecas);
  const mudados = useMemo(() => new Set(estadoMapa.mudancas.map((m) => m.id)), [estadoMapa.mudancas]);
  const lotesFiltrados = useMemo(
    () => agruparPorLote(d.filtrado.demandas, { pecas: d.pecas }),
    [d.filtrado.demandas, d.pecas]
  );
  const apelidos = useMemo(() => apelidosDaPlanilha(dados), [dados]);

  // ---- painéis: um aberto por vez; o último alvo de cada um fica guardado
  // para a animação de saída não piscar um painel vazio.
  const [aberto, setAberto] = useState(PAINEL_FECHADO);
  const [alvos, setAlvos] = useState({
    ambiente: null,
    tarefa: null,
    lote: null,
    peca: null,
    novaTarefa: null,
    desmembrar: null,
    aba: ABA_PADRAO,
  });
  const [ficha, setFicha] = useState(null);
  const abrir = useCallback((tipo, parcial) => {
    setAlvos((a) => ({ ...a, ...parcial }));
    setAberto(tipo);
  }, []);
  const fechar = useCallback(() => setAberto(PAINEL_FECHADO), []);

  const abrirAmbiente = useCallback(
    (demanda) => abrir("ambiente", { ambiente: { chaveLinha: demanda.chaveLinha, chave: demanda.chave } }),
    [abrir]
  );
  const abrirTarefa = useCallback((tarefa) => abrir("tarefa", { tarefa: tarefa.id }), [abrir]);
  const abrirLote = useCallback((lote) => abrir("lote", { lote: lote.chave }), [abrir]);
  const abrirPeca = useCallback((peca) => abrir("peca", { peca: peca.id }), [abrir]);
  const abrirNovaTarefa = useCallback((inicial = {}) => abrir("nova-tarefa", { novaTarefa: inicial }), [abrir]);
  const abrirDesmembrar = useCallback(
    (demanda) => abrir("desmembrar", { desmembrar: { chaveLinha: demanda.chaveLinha, chave: demanda.chave } }),
    [abrir]
  );
  const abrirConfiguracao = useCallback((aba = ABA_PADRAO) => abrir("configurar", { aba }), [abrir]);
  useAbrirConfiguracaoPelaNavegacao(Boolean(setor), podeGerir, abrirConfiguracao);

  const acharDemanda = useCallback(
    (alvo) => {
      if (!alvo) return null;
      return (
        d.doSetor.find((x) => x.chaveLinha === alvo.chaveLinha) ||
        (alvo.chave ? d.doSetor.find((x) => x.chave === alvo.chave) : null) ||
        null
      );
    },
    [d.doSetor]
  );
  const demandaAberta = useMemo(() => acharDemanda(alvos.ambiente), [acharDemanda, alvos.ambiente]);
  const demandaDoDesmembrar = useMemo(() => acharDemanda(alvos.desmembrar), [acharDemanda, alvos.desmembrar]);
  const tarefasPorId = useMemo(
    () => new Map([...(historico || []), ...estado.tarefas].map((t) => [t.id, t])),
    [historico, estado.tarefas]
  );
  const tarefaAbertaNoPainel = alvos.tarefa ? tarefasPorId.get(alvos.tarefa) || null : null;
  const loteAberto = useMemo(
    () =>
      alvos.lote
        ? agruparPorLote(d.doSetor, { pecas: d.pecas }).find((l) => l.chave === alvos.lote) || null
        : null,
    [alvos.lote, d.doSetor, d.pecas]
  );
  // A peça é buscada em TODAS: ela pode ter andado para outro setor com o
  // painel aberto, e fechá-lo na cara do gestor seria pior que mostrá-la.
  const pecaAberta = useMemo(
    () => (alvos.peca ? d.todasPecas.find((p) => p.id === alvos.peca) || null : null),
    [alvos.peca, d.todasPecas]
  );
  const tarefasDoAmbienteAberto = useMemo(
    () => (demandaAberta?.chave ? estado.tarefas.filter((t) => t.ambiente_chave === demandaAberta.chave) : []),
    [demandaAberta, estado.tarefas]
  );
  const pecasDoAmbienteAberto = useMemo(
    () => (demandaAberta?.chave ? pecasDoAmbienteChave(d.todasPecas, demandaAberta.chave) : []),
    [demandaAberta, d.todasPecas]
  );
  const tarefasDaPecaAberta = useMemo(
    () => (pecaAberta ? estado.tarefas.filter((t) => t.peca_id === pecaAberta.id) : []),
    [pecaAberta, estado.tarefas]
  );
  const demandaPorChave = useCallback((chave) => d.doSetor.find((x) => x.chave === chave) || null, [d.doSetor]);

  const abrirPorAlvo = useCallback(
    (alvo) => {
      if (alvo?.tipo === "tarefa") abrirTarefa(alvo.item || alvo.tarefa);
      else if (alvo?.tipo === "lote") abrirLote(alvo.item || alvo.lote);
      else if (alvo?.tipo === "peca") abrirPeca(alvo.item || alvo.peca);
      else if (alvo) abrirAmbiente(alvo.item || alvo.demanda);
    },
    [abrirAmbiente, abrirLote, abrirPeca, abrirTarefa]
  );
  const abrirDaTimeline = useCallback(
    (item) => {
      if (item.pecaId) {
        const peca = d.todasPecas.find((p) => p.id === item.pecaId);
        if (peca) {
          abrirPeca(peca);
          return;
        }
      }
      if (item.tarefaId && tarefasPorId.has(item.tarefaId)) {
        abrirTarefa(tarefasPorId.get(item.tarefaId));
        return;
      }
      const demanda = item.chave ? demandaPorChave(item.chave) : null;
      if (demanda) abrirAmbiente(demanda);
    },
    [abrirAmbiente, abrirPeca, abrirTarefa, demandaPorChave, tarefasPorId, d.todasPecas]
  );
  const podeAbrirDaTimeline = useCallback(
    (item) => {
      if (item.pecaId) return d.todasPecas.some((p) => p.id === item.pecaId);
      if (item.tarefaId) return tarefasPorId.has(item.tarefaId);
      return Boolean(item.chave && demandaPorChave(item.chave));
    },
    [demandaPorChave, tarefasPorId, d.todasPecas]
  );

  const verFicha = useCallback((demanda) => {
    setFicha({ ambiente: demanda.row[F.AMBIENTE], lote: demanda.row[F.NUMERO_DO_LOTE] ?? "Sem lote", row: demanda.row });
    setAberto(PAINEL_FECHADO);
  }, []);
  const fecharFicha = useCallback(() => {
    setFicha(null);
    setAberto("ambiente");
  }, []);

  const { limparConflito } = acoes;
  const { recarregar: lerEstadoDeNovo } = estadoMapa;
  // Devolve a promessa da leitura: quem precisa da lista nova espera por ela.
  const recarregar = useCallback(() => {
    limparConflito();
    return lerEstadoDeNovo();
  }, [limparConflito, lerEstadoDeNovo]);

  // ---- kanban
  const soltar = useCallback(
    async (item, grupo) => {
      const destino = { subsetorId: grupo.subsetorId, postoId: grupo.postoId };
      try {
        if (entidade === "tarefa") await acoes.moverTarefa(item, destino);
        else if (entidade === "peca") await acoes.moverPeca(item, { ...destino, setorId });
        else if (entidade === "lote") await acoes.alocarVarios(item.demandas, destino);
        else await acoes.alocar(item, destino);
      } catch (e) {
        // 409 já virou o aviso da página (com Recarregar).
        if (!ehConflito(e)) toast.error(e?.message || "Não foi possível mover.");
      }
    },
    [acoes, entidade, setorId, toast]
  );

  const configuracaoDoKanban = useMemo(() => {
    const comum = { indice: d.indice, atividades: d.atividades, hoje: d.hoje };
    if (entidade === "tarefa") {
      return {
        chaveDo: (t) => `tarefa:${t.id}`,
        rotuloDo: (t) => `${t.codigo} ${t.titulo}, ${ROTULO_STATUS_TAREFA[t.status]}`,
        estadoDo: (t) => ({ atrasado: tarefaAtrasada(t, d.hoje), encerrado: !tarefaAberta(t) }),
        podeArrastar: (t) => podeOperar && tarefaAberta(t),
        renderCartao: (t) => <ConteudoTarefa tarefa={t} {...comum} />,
        mudou: (t) => mudados.has(t.id),
        onAbrir: abrirTarefa,
      };
    }
    if (entidade === "peca") {
      return {
        chaveDo: (p) => `peca:${p.id}`,
        rotuloDo: (p) => `${p.codigo} ${p.nome}, ${ROTULO_STATUS_PECA[p.status]}${p.atrasada ? ", atrasada" : ""}`,
        estadoDo: (p) => ({ atrasado: p.atrasada, encerrado: !p.fora }),
        // Peça encerrada (voltou, concluiu, cancelada) não se move: ela já
        // saiu de circulação.
        podeArrastar: (p) => podeOperar && p.fora,
        renderCartao: (p) => <ConteudoPeca peca={p} {...comum} />,
        mudou: (p) => mudados.has(p.id),
        onAbrir: abrirPeca,
      };
    }
    if (entidade === "lote") {
      return {
        chaveDo: (l) => `lote:${l.grupoId}:${l.chave}`,
        rotuloDo: (l) => `${l.rotulo}, ${plural(l.demandas.length, "ambiente", "ambientes")}`,
        estadoDo: (l) => ({ atrasado: l.atrasadas > 0, encerrado: false }),
        podeArrastar: (l) => podeOperar && l.demandas.some((x) => x.chave),
        renderCartao: (l) => <ConteudoLote lote={l} hoje={d.hoje} />,
        mudou: (l) => l.demandas.some((x) => mudados.has(x.chave)),
        onAbrir: abrirLote,
      };
    }
    return {
      chaveDo: (x) => x.chaveLinha,
      rotuloDo: (x) =>
        `${x.rotulo || x.ambiente}, ${ROTULO_STATUS_AMBIENTE[x.statusApp]}${x.atrasada ? ", atrasado" : ""}`,
      estadoDo: (x) => ({ atrasado: x.atrasada, encerrado: false }),
      podeArrastar: (x) => podeOperar && Boolean(x.chave),
      renderCartao: (x) => <ConteudoAmbiente demanda={x} {...comum} />,
      mudou: (x) => Boolean(x.chave) && mudados.has(x.chave),
      onAbrir: abrirAmbiente,
    };
  }, [entidade, d.indice, d.atividades, d.hoje, podeOperar, mudados, abrirTarefa, abrirLote, abrirPeca, abrirAmbiente]);

  // ---- estados de borda
  if (initialLoading || esperando) return <PageLoader />;
  const nomeUsuario = usuario?.nome || usuario?.email || "usuário";

  if (!setor) {
    const existentes = catalogo.setores.slice(0, MAX_SETORES_NO_ERRO);
    return (
      <Pagina>
        <EmptyState
          titulo={catalogo.vazio ? "Nenhum setor cadastrado" : "Setor não encontrado"}
          descricao={
            catalogo.vazio
              ? "Os setores do Mapa são desenhados no Mapa da Produção: crie a primeira sala lá e ligue-a a uma etapa da planilha."
              : "Este setor não existe mais (pode ter sido renomeado ou removido). Escolha um dos setores abaixo."
          }
          acoes={[
            { label: "Voltar ao Mapa da Produção", onClick: () => navigate(ROTA_MAPA) },
            ...existentes.map((s) => ({
              label: s.nome,
              onClick: () => navigate(`${ROTA_MAPA}/setor/${s.id}`),
            })),
          ]}
        />
      </Pagina>
    );
  }
  if (!Array.isArray(dados) || dados.length === 0) {
    return (
      <Pagina>
        <EmptyState
          tone="error"
          titulo="Sem planilha carregada"
          descricao={
            (erroPlanilha ? `${erroPlanilha} ` : "") +
            "O setor lê a mesma planilha do Legado. Carregue-a em Configurações › Planilhas."
          }
          acoes={[{ label: "Abrir Configurações › Planilhas", onClick: () => navigate(ROTA_PLANILHAS) }]}
        />
      </Pagina>
    );
  }

  const kpis = montarKpis(
    d.resumo,
    { noFiltro: d.filtrado.demandas.length, filtrando: d.filtrando },
    {
      verEntidade: (valor) => atualizarPreferencias({ entidade: valor }),
      filtrar: definirFiltro,
      configurarColaboradores: podeGerir ? () => abrirConfiguracao("colaboradores") : undefined,
    }
  );

  const contagemEntidade = {
    ambiente: d.filtrado.demandas.length,
    lote: lotesFiltrados.length,
    tarefa: (visao === "kanban" || visao === "mapa" ? d.filtrado.tarefas : tarefasDaLista).length,
    peca: d.filtrado.pecas.length,
  };

  const alternarColaborador = (id) => {
    const atuais = filtros.colaboradores || [];
    definirFiltro("colaboradores", atuais.includes(id) ? atuais.filter((x) => x !== id) : [...atuais, id]);
  };
  // A busca fica (está à vista na barra) e o filtro de setor do mapa geral
  // também: aqui ele não vale nem aparece, então "Limpar" não o apaga.
  const limparFiltrosDoPainel = () => substituirFiltros({ busca: filtros.busca, setores: filtros.setores });
  const contextoFiltros = { demandas: d.doSetor, estado: d.estadoDoSetor, catalogo };

  const conteudo = () => {
    if (visao === "mapa") {
      return (
        <MapaDoSetor
          setor={setor}
          estado={estado}
          demandas={d.filtrado.demandas}
          tarefas={d.filtrado.tarefas}
          pecas={d.filtrado.pecas}
          indice={d.indice}
          hoje={d.hoje}
          podeEditar={podeGerir}
          modo={preferencias.modoMapa}
          onModo={(modoMapa) => atualizarPreferencias({ modoMapa })}
          acoes={acoes}
          onRecarregar={recarregar}
          onAbrirAmbiente={abrirAmbiente}
          onAbrirPeca={abrirPeca}
        />
      );
    }
    if (visao === "tabela") {
      return (
        <Tabela
          entidade={entidade}
          setorId={setorId}
          setorNome={setor.nome}
          demandas={d.filtrado.demandas}
          tarefas={tarefasDaLista}
          pecas={pecasDaLista}
          indice={d.indice}
          hoje={d.hoje}
          colunasSalvas={preferencias.colunas[entidade]}
          onMudarColunas={(keys) =>
            atualizarPreferencias({ colunas: { ...preferencias.colunas, [entidade]: keys } })
          }
          onAbrir={abrirPorAlvo}
        />
      );
    }
    if (visao === "calendario") {
      return (
        <CalendarioSetor
          entidade={entidade}
          demandas={d.filtrado.demandas}
          tarefas={tarefasDaLista}
          pecas={pecasDaLista}
          hoje={d.hoje}
          base={preferencias.baseCalendario}
          onMudarBase={(baseCalendario) => atualizarPreferencias({ baseCalendario })}
          onAbrir={abrirPorAlvo}
        />
      );
    }
    if (visao === "timeline") {
      return (
        <TimelineSetor
          setorId={setorId}
          entidade={entidade}
          demandas={d.filtrado.demandas}
          tarefas={tarefasDaLista}
          pecas={pecasDaLista}
          filtrando={d.filtrando}
          atualizacao={estado}
          hoje={d.hoje}
          onAbrir={abrirDaTimeline}
          podeAbrir={podeAbrirDaTimeline}
        />
      );
    }
    return (
      <>
        {colunas.length === 1 && (
          <Aviso $tom="neutral" data-convite="sim" role="status">
            <span>
              {setor.nome} ainda não tem subsetores. Crie subsetores e postos para distribuir os ambientes e ver
              quem está em cada lugar.
            </span>
            {podeGerir && (
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => abrirConfiguracao("subsetores")}>
                Criar subsetores
              </button>
            )}
          </Aviso>
        )}
        <Kanban
          colunas={colunas}
          grupos={grupos}
          indice={d.indice}
          atividades={d.atividades}
          onSoltar={soltar}
          onNovaTarefa={
            podeOperar
              ? (grupo) => abrirNovaTarefa({ subsetorId: grupo.subsetorId, postoId: grupo.postoId })
              : undefined
          }
          animar={animacoes}
          {...configuracaoDoKanban}
        />
      </>
    );
  };

  return (
    <MotionConfig reducedMotion={animacoes ? "user" : "always"}>
      <Pagina>
        <CabecalhoDoSetor
          setor={setor}
          resumo={d.resumo}
          meta={meta}
          nomeUsuario={nomeUsuario}
          kpis={kpis}
          onVoltar={() => navigate(ROTA_MAPA)}
          onConfigurar={podeGerir ? () => abrirConfiguracao() : undefined}
          onNovaTarefa={podeOperar ? () => abrirNovaTarefa() : undefined}
        />

        {acoes.conflito && (
          <Aviso role="alert">
            <span>{acoes.conflito}</span>
            <button type="button" className="btn btn--secondary btn--sm" onClick={recarregar}>
              Recarregar
            </button>
          </Aviso>
        )}

        <Equipe
          colaboradores={d.colaboradores}
          atividades={d.atividades}
          selecionados={filtros.colaboradores || []}
          onAlternar={alternarColaborador}
          onCadastrar={podeGerir ? () => abrirConfiguracao("colaboradores") : undefined}
        />

        <BarraDoSetor
          entidade={entidade}
          visao={visao}
          contagens={contagemEntidade}
          onEntidade={(valor) => atualizarPreferencias({ entidade: valor })}
          onVisao={(valor) => atualizarPreferencias({ visao: valor })}
          filtros={filtros}
          definirFiltro={definirFiltro}
          onLimparFiltros={limparFiltrosDoPainel}
          contextoFiltros={contextoFiltros}
          resultadoFiltros={{
            ambientes: d.filtrado.demandas.length,
            tarefas: d.filtrado.tarefas.length,
            pecas: d.filtrado.pecas.length,
          }}
          estadoMapa={estadoMapa}
        />

        <ChipsDosFiltros
          filtros={filtros}
          contexto={contextoFiltros}
          onRemover={removerFiltro}
          onLimpar={limparFiltrosDoPainel}
        />

        {conteudo()}

        <DetalheAmbiente
          aberto={aberto === "ambiente"}
          demanda={demandaAberta}
          indice={d.indice}
          estado={estado}
          tarefasDoAmbiente={tarefasDoAmbienteAberto}
          pecasDoAmbiente={pecasDoAmbienteAberto}
          podeOperar={podeOperar}
          hoje={d.hoje}
          acoes={acoes}
          onFechar={fechar}
          onAbrirTarefa={abrirTarefa}
          onNovaTarefa={(demanda) =>
            abrirNovaTarefa({ ambiente: demanda, subsetorId: demanda.subsetorId, postoId: demanda.postoId })
          }
          onAbrirPeca={abrirPeca}
          onDesmembrarPeca={abrirDesmembrar}
          onVerFicha={verFicha}
          onRecarregar={recarregar}
        />
        <DetalheTarefa
          aberto={aberto === "tarefa"}
          tarefa={tarefaAbertaNoPainel}
          indice={d.indice}
          estado={estado}
          setorNome={catalogo.nome(tarefaAbertaNoPainel?.setor_id) || setor.nome}
          podeOperar={podeOperar}
          podeGerir={podeGerir}
          hoje={d.hoje}
          acoes={acoes}
          onFechar={fechar}
          onAbrirAmbiente={(chave) => {
            const demanda = demandaPorChave(chave);
            if (demanda) abrirAmbiente(demanda);
            else toast.info("Este ambiente não está neste setor agora.");
          }}
          onAbrirPeca={(peca) => {
            const combinada = d.todasPecas.find((p) => p.id === peca.id);
            if (combinada) abrirPeca(combinada);
            else toast.info("Esta peça não está mais na lista.");
          }}
          onRecarregar={recarregar}
        />
        <DetalhePeca
          aberto={aberto === "peca"}
          peca={pecaAberta}
          indice={d.indice}
          estado={estado}
          catalogo={catalogo}
          tarefasDaPeca={tarefasDaPecaAberta}
          podeOperar={podeOperar}
          podeGerir={podeGerir}
          hoje={d.hoje}
          acoes={acoes}
          onFechar={fechar}
          onAbrirTarefa={abrirTarefa}
          onNovaTarefa={(peca) =>
            abrirNovaTarefa({ peca, subsetorId: peca.subsetorId, postoId: peca.postoId, colaboradorId: peca.colaboradorId })
          }
          onAbrirAmbiente={(chave) => {
            const demanda = demandaPorChave(chave);
            if (demanda) abrirAmbiente(demanda);
            else toast.info("O ambiente de origem não está neste setor agora.");
          }}
          onRecarregar={recarregar}
        />
        <DetalheLote
          aberto={aberto === "lote"}
          lote={loteAberto}
          setorId={setorId}
          setorNome={setor.nome}
          estado={estado}
          podeOperar={podeOperar}
          hoje={d.hoje}
          acoes={acoes}
          onFechar={fechar}
          onAbrirAmbiente={abrirAmbiente}
          onRecarregar={recarregar}
        />
        <NovaTarefa
          aberto={aberto === "nova-tarefa"}
          inicial={alvos.novaTarefa}
          setorId={setorId}
          setorNome={setor.nome}
          estado={estado}
          demandas={d.doSetor}
          acoes={acoes}
          onFechar={fechar}
          onCriada={fechar}
          onRecarregar={recarregar}
        />
        <DesmembrarPeca
          aberto={aberto === "desmembrar"}
          demanda={demandaDoDesmembrar}
          setorId={setorId}
          estado={estado}
          acoes={acoes}
          onFechar={fechar}
          onCriada={(peca) => (peca?.id ? abrirPeca(peca) : fechar())}
          onRecarregar={recarregar}
        />
        {podeGerir && (
          <ConfigurarSetor
            aberto={aberto === "configurar"}
            aba={alvos.aba}
            onMudarAba={(aba) => setAlvos((a) => ({ ...a, aba }))}
            setor={setor}
            catalogo={catalogo}
            estado={estado}
            apelidos={apelidos}
            acoes={acoes}
            onFechar={fechar}
            onRecarregar={recarregar}
          />
        )}
        <AmbienteDetalhesDrawer ambiente={ficha} onClose={fecharFicha} />
      </Pagina>
    </MotionConfig>
  );
}
